import { useEffect, useId, useRef, useState } from 'react';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DoubleArrowLeftIcon,
  DoubleArrowRightIcon,
  DotsHorizontalIcon,
  ReaderIcon,
  BookmarkIcon,
  ResetIcon,
  TrashIcon,
  UploadIcon,
} from '@radix-ui/react-icons';
import type { ClassroomPlacementMode } from '../domain/classroom-lecture';
import { ClassroomControlButton } from './ClassroomControlButton';
import { ClassroomZoomControl } from './ClassroomZoomControl';
import type { HomeworkBoardCorner } from '../domain/homework-board-focus';
import {
  classroomMarkerLabel,
  parseClassroomMarkerCount,
  type ClassroomMarkerMode,
} from '../domain/classroom-marker-display';

export interface ClassroomSourceRoutes {
  readonly routes: readonly { readonly id: string; readonly label: string }[];
  readonly currentId: string;
  readonly onSelect: (id: string) => void;
}

export function ClassroomMarkerOptions({
  markerMode,
  customMarkerCount,
  onSelect,
  onCustomChange,
  presentation = 'studio',
}: {
  readonly markerMode: ClassroomMarkerMode;
  readonly customMarkerCount: number;
  readonly onSelect: (mode: ClassroomMarkerMode, count?: number) => void;
  readonly onCustomChange: (count: number) => void;
  readonly presentation?: 'studio' | 'list';
}) {
  const [custom, setCustom] = useState(markerMode === 'CUSTOM');
  const [draft, setDraft] = useState(String(customMarkerCount));
  const inputRef = useRef<HTMLInputElement>(null);
  const optionsRef = useRef<HTMLDivElement>(null);
  const count = parseClassroomMarkerCount(draft);
  useEffect(() => {
    if (custom) inputRef.current?.focus();
    else optionsRef.current?.querySelector<HTMLButtonElement>('button')?.focus();
  }, [custom]);
  return (
    <div className="studio-marker-options" role="group" aria-label="手数显示范围" ref={optionsRef}>
      {presentation === 'studio' ? <strong>手数显示</strong> : null}
      {(
        [
          ['LAST_ONE', '1'],
          ['LAST_FIVE', '5'],
          ['LAST_TEN', '10'],
          ['ALL', '全部'],
        ] as const
      ).map(([mode, label]) => (
        <button
          type="button"
          key={mode}
          aria-pressed={markerMode === mode}
          onClick={() => onSelect(mode)}
        >
          <span>
            {presentation === 'list' ? classroomMarkerLabel(mode, customMarkerCount) : label}
          </span>
          {markerMode === mode ? (
            presentation === 'list' ? (
              <small>当前</small>
            ) : (
              <CheckIcon aria-hidden="true" />
            )
          ) : null}
        </button>
      ))}
      <button
        type="button"
        aria-pressed={markerMode === 'CUSTOM'}
        aria-expanded={custom}
        onClick={() => setCustom(!custom)}
      >
        <span>自定义</span>
        {markerMode === 'CUSTOM' ? (
          presentation === 'list' ? (
            <small>当前</small>
          ) : (
            <CheckIcon aria-hidden="true" />
          )
        ) : null}
      </button>
      {custom ? (
        <form
          className="studio-marker-custom"
          onSubmit={(event) => {
            event.preventDefault();
            if (count !== null) onSelect('CUSTOM', count);
          }}
        >
          <label>
            显示最近几手
            <input
              ref={inputRef}
              type="number"
              min="1"
              step="1"
              inputMode="numeric"
              aria-label="自定义显示手数"
              aria-invalid={count === null}
              value={draft}
              onChange={(event) => {
                setDraft(event.currentTarget.value);
                const next = parseClassroomMarkerCount(event.currentTarget.value);
                if (next !== null) onCustomChange(next);
              }}
            />
          </label>
          {count === null ? <small role="alert">请输入大于 0 的整数</small> : null}
          <button type="submit" disabled={count === null}>
            确定
          </button>
        </form>
      ) : null}
    </div>
  );
}

export function ClassroomStudioMore({
  busy,
  annotationOpen,
  markerMode,
  customMarkerCount,
  onMarkerMode,
  onRecords,
  onUploadSgf,
  onTextbooks,
  onClear,
  sourceRoutes,
}: {
  readonly busy: boolean;
  readonly annotationOpen: boolean;
  readonly markerMode: ClassroomMarkerMode;
  readonly customMarkerCount: number;
  readonly onMarkerMode: (mode: ClassroomMarkerMode, count?: number) => void;
  readonly onRecords: () => void;
  readonly onUploadSgf: () => void;
  readonly onTextbooks?: (() => void) | undefined;
  readonly onClear: () => void;
  readonly sourceRoutes?: ClassroomSourceRoutes | undefined;
}) {
  const [open, setOpen] = useState(false);
  const [showMarkers, setShowMarkers] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const panelId = useId();
  const close = () => {
    setOpen(false);
    setConfirmClear(false);
    setShowMarkers(false);
    triggerRef.current?.focus();
  };
  useEffect(() => {
    if (!open) return;
    panelRef.current?.querySelector<HTMLButtonElement>('button:not(:disabled)')?.focus();
    const outside = (event: PointerEvent) => {
      if (event.target instanceof Node && !rootRef.current?.contains(event.target)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('keydown', escape);
    };
  }, [open, confirmClear]);

  return (
    <div
      className={`studio-more${showMarkers ? ' is-marker-open' : ''}`}
      ref={rootRef}
      onBlur={(event) => {
        if (
          event.relatedTarget instanceof Node &&
          !event.currentTarget.contains(event.relatedTarget)
        )
          setOpen(false);
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        className={open ? 'is-active' : ''}
        onClick={() => {
          setOpen(!open);
          setConfirmClear(false);
          setShowMarkers(false);
        }}
      >
        <DotsHorizontalIcon aria-hidden="true" />
        <span>更多</span>
      </button>
      {open ? (
        <div
          ref={panelRef}
          id={panelId}
          className="studio-more-panel"
          role="dialog"
          aria-label={confirmClear ? '清空棋盘确认' : '更多课堂工具'}
        >
          {confirmClear ? (
            <div className="studio-clear-confirm">
              <strong>清空当前棋盘？</strong>
              <p>棋子、试下变化、标记和已载入的棋谱将清空。课堂笔记会保留。</p>
              <div>
                <button type="button" onClick={() => setConfirmClear(false)}>
                  取消
                </button>
                <button
                  type="button"
                  className="studio-danger"
                  disabled={busy}
                  onClick={() => {
                    onClear();
                    close();
                  }}
                >
                  确认清空
                </button>
              </div>
            </div>
          ) : (
            <>
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  onRecords();
                  close();
                }}
              >
                <ReaderIcon aria-hidden="true" />
                <span>野狐棋谱</span>
                <ChevronRightIcon aria-hidden="true" />
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  close();
                  onUploadSgf();
                }}
              >
                <UploadIcon aria-hidden="true" />
                <span>上传SGF</span>
                <ChevronRightIcon aria-hidden="true" />
              </button>
              {onTextbooks ? (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    close();
                    onTextbooks();
                  }}
                >
                  <BookmarkIcon aria-hidden="true" />
                  <span>教材题目</span>
                  <ChevronRightIcon aria-hidden="true" />
                </button>
              ) : null}
              {sourceRoutes !== undefined && sourceRoutes.routes.length > 1 ? (
                <label className="studio-source-routes">
                  原谱路线
                  <select
                    aria-label="选择原谱路线"
                    disabled={busy || annotationOpen}
                    value={sourceRoutes.currentId}
                    onChange={(event) => {
                      sourceRoutes.onSelect(event.target.value);
                      close();
                    }}
                  >
                    {sourceRoutes.routes.map((route) => (
                      <option key={route.id} value={route.id}>
                        {route.label}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
              <div className="studio-menu-divider" />
              <button
                type="button"
                aria-expanded={showMarkers}
                onClick={() => setShowMarkers(!showMarkers)}
              >
                <span className="studio-number-icon" aria-hidden="true">
                  12
                </span>
                <span>手数显示</span>
                <small>{classroomMarkerLabel(markerMode, customMarkerCount)}</small>
                <ChevronLeftIcon aria-hidden="true" />
              </button>
              <div className="studio-menu-divider" />
              <button
                type="button"
                className="studio-danger"
                disabled={busy}
                onClick={() => setConfirmClear(true)}
              >
                <TrashIcon aria-hidden="true" />
                <span>清空棋盘</span>
              </button>
            </>
          )}
        </div>
      ) : null}
      {open && showMarkers && !confirmClear ? (
        <ClassroomMarkerOptions
          markerMode={markerMode}
          customMarkerCount={customMarkerCount}
          onSelect={(mode, count) => {
            onMarkerMode(mode, count);
            close();
          }}
          onCustomChange={(count) => onMarkerMode('CUSTOM', count)}
        />
      ) : null}
    </div>
  );
}

export function ClassroomStudioControls({
  busy,
  actionPending,
  placementMode,
  alternateWhiteFirst,
  alternateHint,
  onAlternate,
  onPlacement,
  annotationOpen,
  zoomed,
  onZoom,
  onZoomCorner,
  researching,
  researchLabel,
  researchAvailable,
  markerMode,
  customMarkerCount,
  onMarkerMode,
  onResearch,
  onRecords,
  onUploadSgf,
  onTextbooks,
  onClear,
  sourceRoutes,
  previousUnavailable,
  nextUnavailable,
  onFirst,
  onPrevious,
  onNext,
  onLast,
  moveNumber,
  totalMoves,
  progressDisabled,
  onProgress,
  aiEnabled,
  aiPending,
  onAi,
}: {
  readonly busy: boolean;
  readonly actionPending: boolean;
  readonly placementMode: ClassroomPlacementMode | null;
  readonly alternateWhiteFirst: boolean;
  readonly alternateHint: '黑先' | '白先' | null;
  readonly onAlternate: () => void;
  readonly onPlacement: (mode: 'BLACK_ONLY' | 'WHITE_ONLY') => void;
  readonly annotationOpen: boolean;
  readonly zoomed: boolean;
  readonly onZoom: () => void;
  readonly onZoomCorner: (corner: HomeworkBoardCorner) => void;
  readonly researching: boolean;
  readonly researchLabel?: string | undefined;
  readonly researchAvailable: boolean;
  readonly markerMode: ClassroomMarkerMode;
  readonly customMarkerCount: number;
  readonly onMarkerMode: (mode: ClassroomMarkerMode, count?: number) => void;
  readonly onResearch: () => void;
  readonly onRecords: () => void;
  readonly onUploadSgf: () => void;
  readonly onTextbooks?: (() => void) | undefined;
  readonly onClear: () => void;
  readonly sourceRoutes?: ClassroomSourceRoutes | undefined;
  readonly previousUnavailable: boolean;
  readonly nextUnavailable: boolean;
  readonly onFirst: () => void;
  readonly onPrevious: () => void;
  readonly onNext: () => void;
  readonly onLast: () => void;
  readonly moveNumber: number;
  readonly totalMoves: number;
  readonly progressDisabled: boolean;
  readonly onProgress: (value: number) => void;
  readonly aiEnabled: boolean;
  readonly aiPending: boolean;
  readonly onAi: () => void;
}) {
  return (
    <div className="studio-controls">
      <div className="studio-tool-row" role="group" aria-label="课堂讲解工具">
        <ClassroomControlButton
          type="button"
          busy={busy}
          disabled={researching || annotationOpen}
          className={placementMode === 'ALTERNATE' ? 'is-active' : ''}
          aria-label={`黑白交替，${alternateWhiteFirst ? '白先' : '黑先'}`}
          aria-pressed={placementMode === 'ALTERNATE'}
          title="黑白交替；再次点击切换先手"
          onClick={onAlternate}
        >
          {alternateHint !== null ? (
            <span className="classroom-placement-hint" role="status">
              {alternateHint}
            </span>
          ) : null}
          <span className="studio-stone-pair" aria-hidden="true">
            <img
              src={`/assets/teacher-lecture/${alternateWhiteFirst ? 'white-shell' : 'black-slate'}.png`}
              alt=""
            />
            <img
              src={`/assets/teacher-lecture/${alternateWhiteFirst ? 'black-slate' : 'white-shell'}.png`}
              alt=""
            />
          </span>
          <span>交替</span>
        </ClassroomControlButton>
        {(['BLACK_ONLY', 'WHITE_ONLY'] as const).map((mode) => (
          <ClassroomControlButton
            type="button"
            key={mode}
            busy={busy}
            disabled={researching || annotationOpen}
            className={placementMode === mode ? 'is-active' : ''}
            aria-pressed={placementMode === mode}
            aria-label={mode === 'BLACK_ONLY' ? '只添加黑子' : '只添加白子'}
            onClick={() => onPlacement(mode)}
          >
            <img
              className="studio-stone-icon"
              src={`/assets/teacher-lecture/${mode === 'BLACK_ONLY' ? 'black-slate' : 'white-shell'}.png`}
              alt=""
            />
            <span>{mode === 'BLACK_ONLY' ? '黑棋' : '白棋'}</span>
          </ClassroomControlButton>
        ))}
        <ClassroomControlButton
          type="button"
          busy={busy}
          disabled={!researchAvailable || annotationOpen}
          aria-pressed={researching}
          className={researching ? 'is-active' : ''}
          title={
            annotationOpen
              ? '请先关闭标记工具'
              : researchAvailable
                ? undefined
                : '请选择黑白交替模式，或载入一盘野狐棋谱'
          }
          onClick={onResearch}
        >
          <ResetIcon aria-hidden="true" />
          <span>{researchLabel ?? (researching ? '结束' : '研究')}</span>
        </ClassroomControlButton>
        <ClassroomZoomControl zoomed={zoomed} onRestore={onZoom} onSelect={onZoomCorner} />
        <ClassroomStudioMore
          busy={actionPending}
          annotationOpen={annotationOpen}
          markerMode={markerMode}
          customMarkerCount={customMarkerCount}
          onMarkerMode={onMarkerMode}
          onRecords={onRecords}
          onUploadSgf={onUploadSgf}
          onTextbooks={onTextbooks}
          onClear={onClear}
          sourceRoutes={sourceRoutes}
        />
      </div>
      <div className="studio-playback-row" role="group" aria-label="课堂棋盘前进与后退">
        <ClassroomControlButton
          type="button"
          busy={busy}
          disabled={previousUnavailable}
          onClick={onFirst}
          aria-label={researching ? '变化起点' : '首手'}
          title={researching ? '变化起点' : '首手'}
        >
          <DoubleArrowLeftIcon aria-hidden="true" />
        </ClassroomControlButton>
        <ClassroomControlButton
          type="button"
          busy={busy}
          disabled={previousUnavailable}
          onClick={onPrevious}
          aria-label="上一手"
        >
          <ArrowLeftIcon aria-hidden="true" />
          <span>上一手</span>
        </ClassroomControlButton>
        <span className="studio-move-number" role="status">
          {researching ? '变化' : '第'} {moveNumber} 手
        </span>
        <ClassroomControlButton
          type="button"
          busy={busy}
          disabled={nextUnavailable}
          onClick={onNext}
          aria-label="下一手"
        >
          <span>下一手</span>
          <ArrowRightIcon aria-hidden="true" />
        </ClassroomControlButton>
        <ClassroomControlButton
          type="button"
          busy={busy}
          disabled={nextUnavailable}
          onClick={onLast}
          aria-label={researching ? '变化末手' : '末手'}
          title={researching ? '变化末手' : '末手'}
        >
          <DoubleArrowRightIcon aria-hidden="true" />
        </ClassroomControlButton>
        <button
          type="button"
          role="switch"
          aria-checked={aiEnabled}
          aria-label="AI分析"
          aria-busy={aiPending}
          className="studio-ai-switch"
          onClick={onAi}
        >
          <span>{aiPending ? '分析中' : 'AI分析'}</span>
          <span className="studio-switch-track" aria-hidden="true">
            <i />
          </span>
        </button>
      </div>
      <input
        className="studio-progress"
        type="range"
        min={0}
        max={Math.max(1, totalMoves)}
        value={moveNumber}
        disabled={progressDisabled}
        aria-disabled={progressDisabled || busy}
        aria-busy={busy}
        aria-label="棋谱进度"
        aria-valuetext={`第 ${moveNumber} 手，共 ${totalMoves} 手`}
        onPointerDown={(event) => {
          if (busy) event.preventDefault();
        }}
        onKeyDown={(event) => {
          if (busy) event.preventDefault();
        }}
        onChange={(event) => {
          // Retain the accepted position and appearance while the server validates a move.
          if (busy || progressDisabled) return;
          onProgress(Number(event.currentTarget.value));
        }}
      />
    </div>
  );
}
